import { Button, Checkbox, Form, Input, message, Select, Switch, TimePicker } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
	getCurrency,
	getObjectValues,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { addressValidatorFormv4 } from '../../utils/LocationUtils';
import { LocationAutocomplete } from '../location_autocomplete/LocationAutocomplete';
import { CURRENCIES, ROLE_TYPES } from '../../utils/DataConstants';
import { capitalize, getContactName } from '../../utils/DataFormatterUtils';
import { imageUploadValidation, normFile } from '../../utils/ImageUtils';
import OWUpload from '../OWUpload';
import { getProtectedImageUri } from '../../utils/FileAccessUtils';
import { Icon as LegacyIcon } from '@ant-design/compatible';
import { DAYS_OF_THE_WEEK } from '../../constants/times';
import moment from 'moment';
import BackButton from '../back_button/BackButton';
import PhoneNumberComponentAntdV4 from '../PhoneNumberComponent/PhoneNumberComponentAntdV4';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	contractorLocationsRestCrudThunksForBuyer,
	contractorLocationsRestCrudThunksForSupplier,
} from '../../thunks/contractor_locations_thunks';
import { usersRestCrudThunksForSupplier } from '../../thunks/users_thunks';

const ContractorLocationForm: FC<any> = ({
	formData,
	userType,
	create,
	update,
	redirectForwardUrl,
	history,
	onSuccess,
	users,
	fetchUsers,
	fetchMultipleUsers,
}): React.ReactElement => {
	const [allPhotos, setAllPhotos] = useState<any[]>();
	const [loading, setLoading] = useState(false);

	const [form] = useForm();

	const formValues = Form.useWatch([], form);

	const { setFieldsValue, validateFields, getFieldValue } = form;

	const initialValues = useMemo(
		() => ({
			...formData,
			'primaryAddress-streetAddress1': nullSafeGet('primaryAddress.streetAddress1', formData),
			'primaryAddress-streetAddress2Opt': nullSafeGet('primaryAddress.streetAddress2Opt', formData),
			'primaryAddress-city': nullSafeGet('primaryAddress.city', formData),
			'primaryAddress-region': nullSafeGet('primaryAddress.region', formData),
			'primaryAddress-postcode': nullSafeGet('primaryAddress.postcode', formData),
			'primaryAddress-country': nullSafeGet('primaryAddress.country', formData),
		}),
		[formData]
	);

	useEffect(() => {
		setAllPhotos(
			nullSafeGetOrElse('images', formData, []).map((p, idx) => {
				const [imgFileId, imgFileName] = p.split('/');
				const thumbUri = getProtectedImageUri(userType)(imgFileId, imgFileName, 100, 100);
				const fullUri = getProtectedImageUri(userType)(imgFileId, imgFileName, 400, 400);
				return {
					uid: idx,
					size: 1,
					name: imgFileName,
					filename: imgFileName,
					url: fullUri,
					status: 'done',
					thumbUrl: thumbUri,
					response: { data: { fileName: imgFileName, fileId: imgFileId } },
					type: 'image/jpeg',
				};
			})
		);
	}, [formData, userType]);

	const createRawAddress = (addr) => {
		if (!addr || addr === undefined) {
			return '';
		}
		return `${addr.streetAddress1}  ${addr.city}, ${addr.region}, ${addr.postcode}, ${addr.country}`;
	};

	const handleLocationAutocompleteSelect = useCallback(
		(addr, lonLat, parsedAddress) => {
			setFieldsValue({
				primaryAddress: parsedAddress,
				'primaryAddress-streetAddress1': parsedAddress.streetAddress1,
				'primaryAddress-streetAddress2Opt': parsedAddress.streetAddress2Opt,
				'primaryAddress-city': parsedAddress.city,
				'primaryAddress-region': parsedAddress.region,
				'primaryAddress-postcode': parsedAddress.postcode,
				'primaryAddress-country': parsedAddress.country,
				longitude: lonLat.lng,
				latitude: lonLat.lat,
			});
			validateFields(['primaryAddress']);
		},
		[setFieldsValue, validateFields]
	);

	const handleImageUpdate = (info) => {
		if (info.file.status === 'done') {
			setAllPhotos(info.fileList);
		} else if (info.file.status === undefined) {
			info.fileList = allPhotos;
		}
	};

	const currencyRecordsList = getObjectValues(CURRENCIES);

	const isImageUploading = nullSafeGetOrElse('images', formValues, []).some(
		(_) => _.status === 'uploading'
	);

	const isUpdate = formData && formData.id !== undefined;
	const submitAction = isUpdate ? update : create;
	const submitText = isUpdate ? 'Update location' : 'Create location';

	const onSubmit = useCallback(
		(values) => {
			const picturesInfo = nullSafeGetOrElse('images', values, []).map((p) => {
				const fileName = nullSafeGetOrElse('response.data.fileName', p, '');
				const fileId = nullSafeGetOrElse('response.data.fileId', p, '');
				return `${fileId}/${fileName}`;
			});

			const operatingHours = DAYS_OF_THE_WEEK.reduce((acc, day) => {
				if (values[`${day}-from`] && values[`${day}-to`]) {
					acc[day] = {
						from: values[`${day}-from`].format('h:mm a'),
						to: values[`${day}-to`].format('h:mm a'),
					};
				}
				return acc;
			}, {});

			const location = {
				...formData,
				...values,
				images: picturesInfo,
				operatingHours,
				currencyId: values.currencyId || 'USD',
			};

			setLoading(true);
			submitAction(location)
				.then((record) => {
					if (redirectForwardUrl) {
						history.push(redirectForwardUrl);
					}
					if (onSuccess) {
						onSuccess(record);
					}
				})
				.catch(message.error)
				.finally(() => setLoading(false));
		},
		[formData, history, onSuccess, redirectForwardUrl, submitAction]
	);

	const clientBuyerCompanyOrTenantName = useMemo(
		() =>
			nullSafeGet('contractorClient.clientBuyerCompany.name', formData) ||
			nullSafeGet('contractorTenant.name', formData),
		[formData]
	);

	return (
		<Form
			form={form}
			id="add-parts-form"
			layout="vertical"
			initialValues={initialValues}
			onFinish={onSubmit}
			style={{ maxWidth: 600 }}
		>
			<Form.Item hidden name="latitude" />
			<Form.Item hidden name="longitude" />
			<Form.Item hidden name="contractorClientId" />
			<Form.Item
				name="name"
				label="Name"
				rules={[
					{
						required: true,
						message: 'Please give the location a name.',
					},
				]}
			>
				<Input />
			</Form.Item>
			<Form.Item
				name="brandName"
				label="Brand Name"
				initialValue={clientBuyerCompanyOrTenantName}
				rules={[
					{
						required: true,
						message: 'Please enter the brand name at this location',
					},
				]}
			>
				<Input disabled={!!clientBuyerCompanyOrTenantName || isUpdate} />
			</Form.Item>
			<Form.Item
				rules={[
					{
						required: true,
						message: 'Please search for and select a valid address',
						validator: addressValidatorFormv4(getFieldValue),
					},
				]}
				name="primaryAddress"
				label="Address"
			>
				<LocationAutocomplete
					disabled={isUpdate}
					initialValue={createRawAddress(nullSafeGet('supplierFacility.primaryAddress', formData))}
					placeholder="Search for and select an address"
					onSelect={handleLocationAutocompleteSelect}
				/>
			</Form.Item>
			<Form.Item
				name="storeId"
				label="Store ID"
				rules={[{ required: true, message: 'Please specify location id.' }]}
			>
				<Input />
			</Form.Item>
			<Form.Item
				name="isActive"
				label="Is Active"
				initialValue={formData.isActive === undefined ? true : formData.isActive}
				valuePropName="checked"
			>
				<Switch />
			</Form.Item>
			<Form.Item label="Default Work Order Subscribers" name="primaryContactsEmails">
				<OWAsyncSelect
					mode="multiple"
					stateSlice={users}
					targetCollectionName="contractorLocationsDropdown"
					fetchMultiple={(ids, targetCollectionName) => {
						fetchMultipleUsers(ids, targetCollectionName);
					}}
					fetchData={(searchText, targetCollectionName) => {
						fetchUsers({ name: searchText }, targetCollectionName);
					}}
					renderRecord={(user) => (
						<Select.Option key={user.id} value={user.id}>
							{getContactName(user.contact)}
						</Select.Option>
					)}
				/>
			</Form.Item>
			<Form.Item
				name="currencyId"
				label="Currency"
				rules={[{ required: true, message: 'Please select a currency for this event.' }]}
				initialValue={getCurrency({ location: formData }).id || 'USD'}
			>
				<Select
					showSearch={true}
					optionFilterProp="children"
					filterOption={(input, option) =>
						currencyRecordsList[option.props.value].displayNamePlural
							.toLowerCase()
							.indexOf(input.toLowerCase()) >= 0
					}
					placeholder="Currency"
				>
					{currencyRecordsList.map((currency) => (
						<Select.Option key={currency.id} value={currency.id}>
							{capitalize(currency.displayNamePlural)} ({currency.id})
						</Select.Option>
					))}
				</Select>
			</Form.Item>
			<PhoneNumberComponentAntdV4
				form={form}
				fieldProps={{
					initialValue: formData.phoneNumber,
					name: 'phoneNumber',
					label: 'Phone Number',
				}}
			/>
			<Form.Item
				name="images"
				label="Photos"
				valuePropName="fileList"
				initialValue={allPhotos}
				getValueFromEvent={normFile}
			>
				<OWUpload
					accept="image/*"
					name="file"
					listType="picture-card"
					className="avatar-uploader"
					multiple={true}
					beforeUpload={imageUploadValidation}
					onChange={handleImageUpdate}
				>
					<div style={{ display: 'inline' }}>
						<LegacyIcon type={'plus'} />
						<div className="ant-upload-text">Upload</div>
					</div>
				</OWUpload>
			</Form.Item>
			<table style={{ marginBottom: 16 }}>
				{DAYS_OF_THE_WEEK.map((day) => {
					const fieldName = `${day}-isOpen`;
					const isOpen = nullSafeGet(`${fieldName}`, formValues);
					return (
						<div style={{ width: 600 }} className="flex-start flex flex-row items-baseline">
							<div style={{ width: 80 }}>
								<span style={{ fontSize: 16 }}>{day}</span>
							</div>
							<div className="flex-start flex flex-row items-baseline">
								<div style={{ paddingRight: 8, paddingLeft: 32 }}>
									<Form.Item
										name={fieldName}
										initialValue={nullSafeGet(`operatingHours.${day}.from`, formData) !== undefined}
										valuePropName="checked"
									>
										<Checkbox />
									</Form.Item>
								</div>

								{isOpen ? (
									[
										<div>
											<Form.Item
												name={`${day}-from`}
												initialValue={
													nullSafeGet(`operatingHours.${day}.from`, formData)
														? moment(nullSafeGet(`operatingHours.${day}.from`, formData), 'h:mm a')
														: undefined
												}
											>
												<TimePicker
													style={{ width: 160 }}
													use12Hours={true}
													format="h:mm a"
													minuteStep={15}
												/>
											</Form.Item>
										</div>,
										<div style={{ paddingRight: 8, paddingLeft: 8 }}>to</div>,
										<div>
											<Form.Item
												name={`${day}-to`}
												initialValue={
													nullSafeGet(`operatingHours.${day}.to`, formData)
														? moment(nullSafeGet(`operatingHours.${day}.to`, formData), 'h:mm a')
														: undefined
												}
											>
												<TimePicker
													style={{ width: 160 }}
													use12Hours={true}
													format="h:mm a"
													minuteStep={15}
												/>
											</Form.Item>
										</div>,
									]
								) : (
									<td>Closed</td>
								)}
							</div>
						</div>
					);
				})}
			</table>
			<Form.Item>
				<Button
					type="primary"
					htmlType="submit"
					loading={loading || isImageUploading}
					className="contactForm__button"
				>
					{submitText}
				</Button>
				<span style={{ marginLeft: '16px' }}>
					<BackButton buttonText="Cancel" />
				</span>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state) => ({
	users: state.users,
	contractorClients: state.contractor_clients,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	update: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorLocationsRestCrudThunksForSupplier.update(entity)
				: contractorLocationsRestCrudThunksForBuyer.update(entity)
		),
	create: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorLocationsRestCrudThunksForSupplier.create(entity)
				: contractorLocationsRestCrudThunksForBuyer.create(entity)
		),
	fetchUsers: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		dispatch(
			usersRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleUsers: (ids, targetCollectionName) =>
		dispatch(usersRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(ContractorLocationForm)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
